import React from "react";
import { Card, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars-2";
import { FaInfoCircle } from "react-icons/fa";
import { resolveFormIcon } from "../utils/formIcons";
import { POS_RESEARCH_INFO } from "../utils/posSalesInfo";
import PosItemCard, {
	ProductPopover,
	buildMedicineResearchPopover,
	getMedicineComposition,
} from "./PosItemCard";
import {
	getInteractionsList,
	getPrimaryUseList,
} from "../../../PharmacyMedicineAdd/utils/itemListFields";
import { getProductMeta, isPopoverPackagingDetail } from "../../../PharmacyMedicineAdd/utils/productMetaFields";
import ProductMetaBadges from "../../../PharmacyMedicineAdd/components/ProductMetaBadges";

const GRID_COL = { xs: 6, sm: 4, lg: 3 };

const hideHorizontalTrack = (props) => <div {...props} style={{ display: "none" }} />;

const PosInfoTip = ({ text }) => (
	<OverlayTrigger
		placement="top"
		overlay={
			<Tooltip className="pos-research-tooltip text-start">{text}</Tooltip>
		}
	>
		<button type="button" className="pos-research-info-btn" aria-label="Info">
			<FaInfoCircle size={12} />
		</button>
	</OverlayTrigger>
);

const getStockPaneClasses = (showResearchSection, showStockSection) => {
	if (!showResearchSection) {
		return "tw-flex-1 tw-min-h-0 tw-h-full";
	}
	if (!showStockSection) {
		return "tw-flex-shrink-0";
	}
	return "tw-flex-1 tw-min-h-0 tw-max-h-[50%]";
};

const getResearchPaneClasses = (showStockSection) => {
	if (!showStockSection) {
		return "tw-flex-1 tw-min-h-0 tw-h-full";
	}
	return "tw-flex-1 tw-min-h-0 tw-max-h-[50%]";
};

const GridScrollPane = ({ children, paneClassName, fillHeight = true }) => {
	if (!fillHeight) {
		return <div className={`pos-grid-pane-static ${paneClassName || ""}`.trim()}>{children}</div>;
	}

	return (
		<Scrollbars
			autoHide
			className={`pos-product-grid-scroll tw-h-full tw-min-h-0 ${paneClassName || ""}`.trim()}
			style={{ height: "100%", width: "100%" }}
			universal
			renderTrackHorizontal={hideHorizontalTrack}
		>
			{children}
		</Scrollbars>
	);
};

const getProductSubtitle = (product) =>
	product.isOTC ? product.productForm : product.saltName;

const buildProductPopover = (product, t) => {
	const subtitle = getProductSubtitle(product);
	const primaryUse = getPrimaryUseList(product).join(", ");
	const interactions = getInteractionsList(product).join(", ");
	const { packagingDetail } = getProductMeta(product);
	const popoverPackagingDetail =
		packagingDetail && isPopoverPackagingDetail(packagingDetail) ? packagingDetail : "";

	return (
		<ProductPopover
			id={`pos-product-${product._id}`}
			title={product.brandName}
			meta={
				popoverPackagingDetail ? (
					<ProductMetaBadges
						packagingDetail={popoverPackagingDetail}
						className="pos-popover-meta"
					/>
				) : null
			}
			fields={[
				subtitle && {
					label: product.isOTC ? t("Form") : t("Composition"),
					value: subtitle,
				},
				product.manufacturer && {
					label: t("Manufacturer"),
					value: product.manufacturer,
				},
				primaryUse && {
					label: t("Primary Use"),
					value: primaryUse,
				},
				interactions && {
					label: t("Interactions"),
					value: interactions,
				},
				{
					label: t("Stock"),
					value:
						product.totalRemainingMedicine === 0
							? t("Out of Stock")
							: product.totalRemainingMedicine,
					className: `pos-item-grid-popover-stock ${
						product.totalRemainingMedicine === 0 ? "is-out" : ""
					}`,
				},
				packagingDetail && {
					label: t("Packaging Detail"),
					value: packagingDetail,
				},
			].filter(Boolean)}
		/>
	);
};

const StockSectionContent = ({ t, products, showStockSection, onProductClick }) => (
	<div className="pos-grid-section pos-grid-section--stock">
		<div className="pos-grid-section-label">
			<span>{t("In Stock")}</span>
			<small className="text-muted">{products.length}</small>
		</div>
		{showStockSection ? (
			<Row className="g-2 pos-grid-row w-100">
				{products.map((product) => {
					const isOutOfStock = product.totalRemainingMedicine === 0;
					return (
						<Col {...GRID_COL} key={product._id} className="d-flex">
							<PosItemCard
								icon={resolveFormIcon(product)}
								title={product.brandName}
								subtitle={getProductSubtitle(product)}
								stockQty={product.totalRemainingMedicine}
								meta={getProductMeta(product)}
								isOutOfStock={isOutOfStock}
								onClick={() => {
									if (!isOutOfStock) onProductClick(product);
								}}
								popover={buildProductPopover(product, t)}
							/>
						</Col>
					);
				})}
			</Row>
		) : (
			<p className="text-muted small mb-3 px-1">{t("No products found")}</p>
		)}
	</div>
);

export const ProductGrid = ({
	t,
	searchRef,
	searchQuery,
	searchInputProps = {},
	products,
	onProductClick,
	researchMedicines,
	researchMedicinesLoading,
	isMedicineResearchActive,
	isOTC,
	setIsOTC,
}) => {
	const showStockSection = products.length > 0;
	const showResearchSection = isMedicineResearchActive;
	const isSplitView = showResearchSection && showStockSection;
	/** Scrollbars with height:100% collapses when pane is content-sized — skip for empty stock + research */
	const stockPaneUsesScroll = showStockSection || !showResearchSection;

	const stockPaneWrapClass = [
		"d-flex",
		"flex-column",
		stockPaneUsesScroll ? "tw-min-h-0 tw-overflow-hidden" : "tw-flex-shrink-0",
		getStockPaneClasses(showResearchSection, showStockSection),
	]
		.filter(Boolean)
		.join(" ");

	const researchPaneWrapClass = [
		"d-flex",
		"flex-column",
		"tw-min-h-0",
		"tw-overflow-hidden",
		getResearchPaneClasses(showStockSection),
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div className="pos-product-grid-wrap d-flex flex-column flex-grow-1 min-h-0">
			<Form.Control
				ref={searchRef}
				type="text"
				className="mb-2 search-product sale-search pos-barcode-search"
				value={searchQuery}
				placeholder={t("Search or scan barcode...")}
				autoFocus
				id="pos-barcode-search"
				{...searchInputProps}
			/>

			<Card className="h-100 d-flex flex-column pos-body pos-product-grid-panel">
				<Card.Body className="h-100 d-flex flex-column pos-product-grid-body p-2 tw-min-h-0 tw-overflow-hidden">
					<div className="pos-product-grid-layout d-flex flex-column flex-grow-1 tw-min-h-0 tw-h-full tw-overflow-hidden">
						{/* In stock — compact when empty; scrollable when items or full-height without research */}
						<div className={stockPaneWrapClass}>
							<GridScrollPane fillHeight={stockPaneUsesScroll}>
								<StockSectionContent
									t={t}
									products={products}
									showStockSection={showStockSection}
									onProductClick={onProductClick}
								/>
							</GridScrollPane>
						</div>

						{showResearchSection && (
							<>
								{/* {isSplitView && (
								)} */}
								<hr className="pos-grid-divider tw-flex-shrink-0 my-1" />
								<div className={researchPaneWrapClass}>
									<div className="pos-research-toolbar">
										<div className="pos-research-toolbar__main">
											<div className="pos-research-toolbar__heading">
												<span className="pos-research-toolbar__title">
													{t(POS_RESEARCH_INFO.researchTitle)}
												</span>
												<span className="pos-research-pill">
													{t(POS_RESEARCH_INFO.researchBadge)}
												</span>
												<PosInfoTip text={t(POS_RESEARCH_INFO.researchTooltip)} />
											</div>
											<span className="pos-research-tagline">
												{t(POS_RESEARCH_INFO.researchTagline)}
											</span>
										</div>
										<div className="pos-research-toolbar__otc">
											<Form.Check
												type="switch"
												id="otc-switch"
												label={t(POS_RESEARCH_INFO.searchOtcLabel)}
												className="custom-switch-wrapper mt-1 pos-research-otc-switch mb-0"
												checked={isOTC}
												onChange={(event) => setIsOTC(event.target.checked)}
											/>
											<PosInfoTip text={t(POS_RESEARCH_INFO.searchOtcTooltip)} />
										</div>
									</div>
									<GridScrollPane>
										<div
											className={`pos-grid-section pos-grid-section--research position-relative ${isSplitView ? "has-divider" : ""
												}`}
										>
											{researchMedicinesLoading && researchMedicines.length === 0 ? (
												<p className="text-muted small mb-0 px-1">
													{t(POS_RESEARCH_INFO.researchLoading)}
												</p>
											) : researchMedicines.length === 0 ? (
												<p className="text-muted small mb-0 px-1">
													{t(POS_RESEARCH_INFO.researchEmpty)}
												</p>
											) : (
												<Row className="g-2 pos-grid-row w-100">
													{researchMedicines.map((medicine) => {
														const cardProduct = {
															_id: medicine._id,
															brandName: medicine.name,
															saltName: getMedicineComposition(medicine),
															productForm: medicine.product_form,
															isOTC: medicine.medicine_type === "otc",
														};
														return (
															<Col {...GRID_COL} key={medicine._id} className="d-flex">
																<PosItemCard
																	icon={resolveFormIcon(cardProduct)}
																	title={medicine.name}
																	subtitle={getMedicineComposition(medicine)}
																	stockQty={0}
																	meta={getProductMeta(medicine)}
																	isResearch
																	disabled
																	popover={buildMedicineResearchPopover(medicine, t)}
																/>
															</Col>
														);
													})}
												</Row>
											)}
										</div>
									</GridScrollPane>
								</div>
							</>
						)}
					</div>
				</Card.Body>
			</Card>
		</div>
	);
};

export default ProductGrid;
